import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import clsx from 'clsx';

interface Props {
  /** Label shown on the chip; tooltipped when it truncates. */
  name: string;
  /** Leading visual (a kind icon, a file-type icon, …) rendered in the chip's square. */
  leading: ReactNode;
  /** Makes the pill body activate a preview / open on click, Enter, or Space. */
  onClick?: () => void;
  /** Control rendered at the chip's top-right corner (remove, re-attach, …). */
  trailing?: ReactNode;
  /** `sm` (40px) for the composer; `lg` (46px) for the roomier chat-history chips. */
  size?: 'sm' | 'lg';
}

/**
 * The monks-ui attachment pill shared by every chat attachment chip — files
 * and project context, in the composer and in chat history — so they all read
 * as one family. Self-provides a TooltipProvider so the name tooltip (shown
 * only when the label truncates) and any `trailing` control tooltips work in
 * both the composer and the history bubble, neither of which guarantees an
 * ancestor provider.
 */
export function AttachmentChip(props: Props) {
  const clickable = !!props.onClick;
  const isLarge = props.size === 'lg';

  const nameRef = useRef<HTMLSpanElement>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);
  useEffect(() => {
    const element = nameRef.current;
    if (element) {
      setIsNameTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [props.name]);

  return (
    <TooltipProvider>
      <div className="group relative inline-flex" data-testid="attachment-chip">
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={clsx(
                'flex items-center gap-2 rounded-lg border border-border-light bg-background pl-1 pr-2 transition-colors hover:border-border',
                isLarge ? 'h-[46px]' : 'h-10 py-2',
                clickable && 'cursor-pointer',
              )}
              {...(clickable
                ? {
                    role: 'button',
                    tabIndex: 0,
                    onClick: props.onClick,
                    onKeyDown: (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        props.onClick?.();
                      }
                    },
                  }
                : {})}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-surface">
                {props.leading}
              </div>
              <span
                ref={nameRef}
                className={clsx(
                  'max-w-[140px] truncate text-foreground',
                  isLarge ? 'text-sm' : 'text-xs leading-4',
                )}
                data-testid="attachment-chip-name"
              >
                {props.name}
              </span>
            </div>
          </TooltipTrigger>
          {isNameTruncated && <TooltipContent>{props.name}</TooltipContent>}
        </Tooltip>
        {props.trailing}
      </div>
    </TooltipProvider>
  );
}
